import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Button,
  Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { KidsClubSettings, DaySchedule, TimeSlot } from '../../types';

interface WeeklyScheduleEditorProps {
  weeklySchedule: KidsClubSettings['weekly_schedule'];
  onSave: (schedule: KidsClubSettings['weekly_schedule']) => Promise<void>;
  loading?: boolean;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<typeof DAYS[number], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const TIME_SLOT_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

/**
 * Weekly Schedule Editor Component
 * Allows admins to configure each day's capacity and time slots independently
 */
const WeeklyScheduleEditor: React.FC<WeeklyScheduleEditorProps> = ({
  weeklySchedule,
  onSave,
  loading = false,
}) => {
  const [localSchedule, setLocalSchedule] = useState<KidsClubSettings['weekly_schedule']>(
    weeklySchedule
  );
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Update local state when settings change
  useEffect(() => {
    setLocalSchedule(weeklySchedule);
  }, [weeklySchedule]);

  const handleDayClosedToggle = (day: typeof DAYS[number]) => {
    setLocalSchedule({
      ...localSchedule,
      [day]: {
        ...localSchedule[day],
        closed: !localSchedule[day].closed,
      },
    });
  };

  const handleDayCapacityChange = (day: typeof DAYS[number], capacity: number) => {
    setLocalSchedule({
      ...localSchedule,
      [day]: {
        ...localSchedule[day],
        capacity: Math.max(0, Math.min(100, capacity)),
      },
    });
  };

  const handleTimeSlotToggle = (
    day: typeof DAYS[number],
    slot: keyof DaySchedule['time_slots']
  ) => {
    setLocalSchedule({
      ...localSchedule,
      [day]: {
        ...localSchedule[day],
        time_slots: {
          ...localSchedule[day].time_slots,
          [slot]: {
            ...localSchedule[day].time_slots[slot],
            enabled: !localSchedule[day].time_slots[slot].enabled,
          },
        },
      },
    });
  };

  const handleTimeSlotTimeChange = (
    day: typeof DAYS[number],
    slot: keyof DaySchedule['time_slots'],
    field: 'start_time' | 'end_time',
    value: string
  ) => {
    setLocalSchedule({
      ...localSchedule,
      [day]: {
        ...localSchedule[day],
        time_slots: {
          ...localSchedule[day].time_slots,
          [slot]: {
            ...localSchedule[day].time_slots[slot],
            [field]: value,
          },
        },
      },
    });
  };

  const handleTimeSlotCapacityChange = (
    day: typeof DAYS[number],
    slot: keyof DaySchedule['time_slots'],
    capacity: number
  ) => {
    setLocalSchedule({
      ...localSchedule,
      [day]: {
        ...localSchedule[day],
        time_slots: {
          ...localSchedule[day].time_slots,
          [slot]: {
            ...localSchedule[day].time_slots[slot],
            capacity: Math.max(0, Math.min(100, capacity)),
          },
        },
      },
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await onSave(localSchedule);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
    } catch (error: any) {
      console.error('Error saving weekly schedule:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save weekly schedule';
      setSaveError(errorMessage);
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
  };

  const renderTimeSlot = (
    day: typeof DAYS[number],
    slotKey: keyof DaySchedule['time_slots'],
    slot: TimeSlot
  ) => {
    const disabled = localSchedule[day].closed;

    return (
      <Box key={slotKey} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={slot.enabled && !disabled}
              onChange={() => handleTimeSlotToggle(day, slotKey)}
              disabled={disabled}
            />
          }
          label={TIME_SLOT_LABELS[slotKey]}
        />
        {slot.enabled && !disabled && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={slot.start_time}
                onChange={(e) =>
                  handleTimeSlotTimeChange(day, slotKey, 'start_time', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={slot.end_time}
                onChange={(e) =>
                  handleTimeSlotTimeChange(day, slotKey, 'end_time', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={slot.capacity}
                onChange={(e) =>
                  handleTimeSlotCapacityChange(day, slotKey, parseInt(e.target.value) || 0)
                }
                onFocus={(e) => e.target.select()}
                inputProps={{ min: 0, max: 100 }}
                disabled={disabled}
                size="small"
                helperText="Max children"
              />
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  const renderDaySchedule = (day: typeof DAYS[number]) => {
    const daySchedule = localSchedule[day];
    const isClosed = daySchedule.closed;

    // Get list of active time slot names
    const activeSlotNames: string[] = [];
    if (daySchedule.time_slots.morning?.enabled) activeSlotNames.push('Morning');
    if (daySchedule.time_slots.afternoon?.enabled) activeSlotNames.push('Afternoon');
    if (daySchedule.time_slots.evening?.enabled) activeSlotNames.push('Evening');

    const activeSlotsText = activeSlotNames.length > 0
      ? activeSlotNames.join(', ')
      : 'No active slots';

    return (
      <Accordion
        key={day}
        sx={{
          mb: 2,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            boxShadow: 2,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            '&.Mui-expanded': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiTypography-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
            <Typography sx={{ fontWeight: 'bold', minWidth: 120, fontSize: '1.1rem' }}>
              {DAY_LABELS[day]}
            </Typography>
            {isClosed ? (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Closed
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Capacity: {daySchedule.capacity} | {activeSlotsText}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'background.default',
            borderLeft: 4,
            borderColor: 'primary.main',
            pt: 3,
            pb: 3,
          }}
        >
          <Box>
            {/* Open/Closed Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={!isClosed}
                  onChange={() => handleDayClosedToggle(day)}
                  color="primary"
                />
              }
              label={isClosed ? 'Closed' : 'Open'}
              sx={{ mb: 2 }}
            />

            {/* Capacity Field */}
            {!isClosed && (
              <TextField
                fullWidth
                label="Daily Capacity"
                type="number"
                value={daySchedule.capacity}
                onChange={(e) => handleDayCapacityChange(day, parseInt(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
                inputProps={{ min: 0, max: 100 }}
                sx={{ mb: 3 }}
                helperText="Maximum number of children for this day"
              />
            )}

            {/* Time Slots */}
            {!isClosed && (
              <>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Time Slots
                  </Typography>
                </Divider>
                {renderTimeSlot(day, 'morning', daySchedule.time_slots.morning)}
                {renderTimeSlot(day, 'afternoon', daySchedule.time_slots.afternoon)}
                {renderTimeSlot(day, 'evening', daySchedule.time_slots.evening)}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          {/* Card Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonthIcon sx={{ mr: 1 }} />
              Weekly Schedule Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure capacity and time slots for each day of the week. Each day can have its own
              unique schedule.
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Each day can be configured independently with custom capacity and time slots. Perfect for
            handling special schedules like reduced Sunday hours.
          </Alert>

          {/* Day Schedule List */}
          <Box>{DAYS.map((day) => renderDaySchedule(day))}</Box>

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save Weekly Schedule'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Sticky Snackbar for save confirmation - stays visible regardless of scroll */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '1rem',
            alignItems: 'center',
          }}
          icon={<CheckCircleIcon fontSize="large" />}
        >
          Weekly schedule saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeeklyScheduleEditor;
